'use client';
// ──────────────────────────────────────────────────────
// ScanDebugPanel — bottom-left debug overlay
// Toggle with "D" key. Shows last barcode + status.
// ──────────────────────────────────────────────────────
import { useState, useEffect } from 'react';

interface DebugInfo {
    barcode: string;
    status: 'idle' | 'loading' | 'ok' | 'not_found' | 'error';
    timestamp: number;
}

export function ScanDebugPanel({ info }: { info: DebugInfo }) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const toggle = (e: KeyboardEvent) => {
            if (e.key === 'D' || e.key === 'd') {
                // Only toggle if not typing in an input
                const tag = (e.target as HTMLElement)?.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA') return;
                setVisible(v => !v);
            }
        };
        window.addEventListener('keydown', toggle);
        return () => window.removeEventListener('keydown', toggle);
    }, []);

    if (!visible) return null;

    const statusColor: Record<string, string> = {
        idle: '#607890',
        loading: '#F59E0B',
        ok: '#10B981',
        not_found: '#EF4444',
        error: '#EF4444',
    };

    const statusLabel: Record<string, string> = {
        idle: '⏸ جاهز',
        loading: '⏳ جاري البحث...',
        ok: '✅ تم العثور',
        not_found: '❌ غير موجود',
        error: '⚠️ خطأ',
    };

    return (
        <div className="fixed bottom-12 left-4 z-[100] pointer-events-none"
            style={{ fontFamily: 'monospace', fontSize: '11px' }}>
            <div className="px-3 py-2 rounded-lg backdrop-blur-md"
                style={{ background: 'rgba(10, 35, 66, 0.9)', border: '1px solid rgba(80, 200, 200, 0.15)' }}>
                <div style={{ color: '#607890' }}>🔧 Debug (D to hide)</div>
                <div style={{ color: '#A0B4C8' }}>
                    Barcode: <span style={{ color: '#50C8C8' }}>{info.barcode || '—'}</span>
                </div>
                <div style={{ color: statusColor[info.status] }}>
                    Status: {statusLabel[info.status]}
                </div>
                {info.timestamp > 0 && (
                    <div style={{ color: '#607890' }}>
                        {new Date(info.timestamp).toLocaleTimeString('en-GB')}
                    </div>
                )}
            </div>
        </div>
    );
}
